import { useId, useState } from 'react';
import { de, REVIEW_NOTE_MIN_LENGTH } from '@fsv/core';
import { useDamageRef, useTenant } from '../data/hooks';
import type { Damage } from '@fsv/data';
import { tx } from '@fsv/data';
import { exactNumberOf } from '@fsv/data';
import { Sheet } from './Sheet';
import { StatusBadge } from './Status';
import './ReasonDialog.css';

/**
 * Reject, merge or accept: each needs a reason the reporter (and, for accept,
 * every pilot) will read (REVIEW_NOTE_MIN_LENGTH, a CHECK in the database). Merge additionally picks the target, suggested
 * from the nearby search; the reason is prefilled with "Duplikat von FSV-…".
 */
/** The reason this dialog generates, so picking another target may replace it (a hand-written one stays). */
const GENERATED_NOTE = /^Duplikat von (?:[A-Z]{2,5}-)?(?:\d{6}|[A-Z]\d{5})$/;

export function ReasonDialog({
  mode,
  report,
  candidates,
  pool,
  onCancel,
  onSubmit,
}: {
  mode: 'reject' | 'merge' | 'accept';
  report: Damage;
  candidates: Damage[];
  /** All damages of the aircraft, to resolve a typed number when nothing is nearby. */
  pool: Damage[];
  onCancel: () => void;
  onSubmit: (input: { note: string; mergeIntoId?: string | undefined }) => void;
}) {
  const id = useId();
  const ref = useDamageRef();
  const { code } = useTenant();
  const first = candidates[0];
  const [targetId, setTargetId] = useState<string | null>(first?.id ?? null);
  const [typed, setTyped] = useState('');
  const [note, setNote] = useState(mode === 'merge' && first ? tx.reason.mergeDefault(ref(first.number)) : '');
  const [touched, setTouched] = useState(false);

  // Another club's code (-1) matches nothing, like the search.
  const typedNumber = exactNumberOf(typed, code);
  const typedTarget = typedNumber !== null ? pool.find((d) => d.number === typedNumber && d.id !== report.id && d.status !== 'verworfen') : undefined;
  const target = mode === 'merge' ? (candidates.find((c) => c.id === targetId) ?? typedTarget ?? null) : null;
  const tooShort = note.trim().length < REVIEW_NOTE_MIN_LENGTH;
  const valid = !tooShort && (mode !== 'merge' || target !== null);

  const pick = (d: Damage) => {
    setTargetId(d.id);
    // Keep a hand-written reason; replace only the generated one.
    if (!note || GENERATED_NOTE.test(note)) setNote(tx.reason.mergeDefault(ref(d.number)));
  };

  return (
    <Sheet
      title={mode === 'reject' ? tx.reason.rejectTitle : mode === 'accept' ? tx.reason.acceptTitle : tx.reason.mergeTitle}
      kicker={
        <span className="row">
          <span className="mono">{ref(report.number)}</span>
          <StatusBadge status={report.status} />
        </span>
      }
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="btn" onClick={onCancel}>
            {tx.app.cancel}
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={!valid}
            onClick={() => onSubmit({ note: note.trim(), mergeIntoId: target?.id })}
          >
            {mode === 'reject' ? de.review.reject : mode === 'accept' ? de.review.accept : de.review.merge}
          </button>
        </>
      }
    >
      <p className="reason__report">{report.title}</p>
      {mode === 'accept' && <p className="field__hint">{tx.reason.acceptHint}</p>}
      {mode === 'merge' && (
        <fieldset className="choice">
          <legend className="field__label">{tx.reason.mergeTarget}</legend>
          {candidates.length > 0 ? (
            <div className="reason__targets">
              {candidates.map((c) => (
                <label key={c.id} className={`reason__target${targetId === c.id ? ' is-on' : ''}`}>
                  <input type="radio" name={`${id}-target`} checked={targetId === c.id} onChange={() => pick(c)} />
                  {c.thumbUrl ? <img src={c.thumbUrl} alt="" /> : <span className="reason__noimg" />}
                  <span className="reason__tinfo">
                    <span className="row">
                      <span className="mono">{ref(c.number)}</span>
                      <StatusBadge status={c.status} />
                    </span>
                    <span>{c.title}</span>
                  </span>
                </label>
              ))}
            </div>
          ) : (
            <div className="field">
              <label className="field__hint" htmlFor={`${id}-num`}>
                {tx.reason.mergeNoCandidates}
              </label>
              <input
                id={`${id}-num`}
                className="input mono"
                value={typed}
                inputMode="text"
                autoComplete="off"
                onChange={(e) => {
                  setTyped(e.target.value);
                  const n = exactNumberOf(e.target.value, code);
                  if (n !== null && n > 0) setNote(tx.reason.mergeDefault(ref(n)));
                }}
              />
              {typedTarget && <p className="field__hint">{typedTarget.title}</p>}
            </div>
          )}
        </fieldset>
      )}
      <div className="field">
        <label className="field__label" htmlFor={`${id}-note`}>
          {de.review.reasonLabel}
        </label>
        <textarea
          id={`${id}-note`}
          className="textarea"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onBlur={() => setTouched(true)}
          aria-invalid={touched && tooShort}
          aria-describedby={`${id}-count`}
          maxLength={1000}
        />
        <p id={`${id}-count`} className={touched && tooShort ? 'field__error' : 'field__hint'}>
          {touched && tooShort ? de.review.reasonTooShort : tx.reason.count(note.trim().length, REVIEW_NOTE_MIN_LENGTH)}
        </p>
      </div>
    </Sheet>
  );
}
